import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { CartLineItem } from '../../interfaces/Cart.model';
import { isEventProduct, isVoucherProduct } from '../../utils/cart';
import { formatPrice } from '../../utils/price';
import { Spinner } from '../common/Spinner';
import { QuantityControls } from './QuantityControls';

interface LineItemProps {
    item: CartLineItem;
    isUpdating: boolean;
    errors: { [key: string]: string };
    onUpdateQuantity: (lineItemId: number, newQty: number) => Promise<void>;
    locale: string;
    standalone: boolean;
}

// Subcomponents
const ItemDetails = memo(({ item, locale }: { item: CartLineItem; locale: string }) => {
    const { t } = useTranslation();
    const product = item.product;

    const getAvailabilityText = (availableCapacity: number) => {
        if (availableCapacity === 0)
            return <span className="text-red-600">{t('cart.soldOut')}</span>;
        if (availableCapacity === 1)
            return <span className="text-orange-600">{t('cart.lastSpot')}</span>;
        return t('cart.availableTickets', { count: availableCapacity });
    };

    return (
        <div className="w-full md:w-1/2">
            <div className="flex gap-4">
                <div className="w-20 h-20 flex-shrink-0">
                    {product.thumbnail ? (
                        <img
                            src={product.thumbnail}
                            alt={product.title}
                            className="w-full h-full object-cover rounded"
                        />
                    ) : (
                        <img
                            src="https://d3r57otuiacdz9.cloudfront.net/logos/fav-2.svg"
                            alt=""
                            className="w-full h-full object-cover rounded"
                        />
                    )}
                </div>

                <div>
                    <h3 className="text-xl font-medium">
                        {isVoucherProduct(product)
                            ? t('voucher.prefix', { description: product.title })
                            : product.title}
                    </h3>
                    <p className="text-gray-600 font-hikou">{formatPrice(item.price, locale)}</p>

                    {isEventProduct(product) && (
                        <div className="text-sm text-gray-600 mt-1">
                            <p className="text-sm text-gray-600 mt-1">
                                {getAvailabilityText(product.availableCapacity)}
                            </p>
                        </div>
                    )}

                    {isVoucherProduct(product) && product.options && (
                        <div className="text-sm mt-2">
                            {product.options.from && (
                                <p>
                                    {t('voucher.from')}: {product.options.from}
                                </p>
                            )}
                            {product.options.to && (
                                <p>
                                    {t('voucher.to')}: {product.options.to}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

const RemoveButton = memo(
    ({ isUpdating, onRemove }: { isUpdating: boolean; onRemove: () => void }) => (
        <button
            type="button"
            onClick={onRemove}
            className="text-red-600 hover:text-red-800 transition-all duration-200 disabled:opacity-50"
            disabled={isUpdating}
            aria-label="Remove item"
        >
            <div className="relative w-5 h-5">
                <div
                    className={`absolute inset-0 transition-opacity duration-200 ${isUpdating ? 'opacity-100' : 'opacity-0'}`}
                >
                    <Spinner color="green" />
                </div>
                <div
                    className={`absolute inset-0 transition-opacity duration-200 ${isUpdating ? 'opacity-0' : 'opacity-100'}`}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        role="img"
                        aria-label="Remove"
                    >
                        <title>Remove</title>
                        <path
                            fillRule="evenodd"
                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                            clipRule="evenodd"
                        />
                    </svg>
                </div>
            </div>
        </button>
    )
);

// Main Component
export const LineItem = memo(
    ({ item, isUpdating, errors, onUpdateQuantity, locale, standalone }: LineItemProps) => {
        const { t } = useTranslation();
        const itemError = errors[item.id];

        const handleRemove = useCallback(() => {
            if (window.confirm(t('cart.confirmRemove'))) {
                onUpdateQuantity(item.id, 0);
            }
        }, [item.id, onUpdateQuantity, t]);

        return (
            <div
                className={`
            flex flex-col p-4 border rounded text-backgroundGreen
            transition-opacity duration-300
            ${isUpdating ? 'opacity-50' : 'opacity-100'}
        `}
            >
                {itemError && (
                    <div
                        className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded relative mb-4"
                        role="alert"
                    >
                        <span className="block sm:inline">{itemError}</span>
                    </div>
                )}

                <div className="flex items-start flex-wrap gap-y-2">
                    <ItemDetails item={item} locale={locale} />

                    <div className="w-1/2 md:w-1/4 pt-2">
                        <QuantityControls
                            item={item}
                            isUpdating={isUpdating}
                            itemError={itemError}
                            onUpdateQuantity={onUpdateQuantity}
                            standalone={standalone}
                        />
                    </div>

                    <div className="w-1/2 md:w-1/4 flex items-center justify-end space-x-4 pt-2">
                        <div>
                            {item.subtotal !== item.total && (
                                <p className="line-through font-medium font-hikou">
                                    {formatPrice(item.subtotal, locale)}
                                </p>
                            )}
                            <p className="font-medium font-hikou">
                                {formatPrice(item.total, locale)}
                            </p>
                        </div>
                        {standalone && (
                            <RemoveButton isUpdating={isUpdating} onRemove={handleRemove} />
                        )}
                    </div>
                </div>
            </div>
        );
    }
);

// Add display names for better debugging
ItemDetails.displayName = 'ItemDetails';
RemoveButton.displayName = 'RemoveButton';
LineItem.displayName = 'LineItem';
